import { useTranslation } from "react-i18next";

import type { ActId } from "../acts";
import type { DiagnosticTabProps } from "../DiagnosticSection";
import { DiagnosisBlock } from "./DiagnosisBlock";
import { EnvironmentBlock } from "./EnvironmentBlock";
import { InvestmentBlock } from "./InvestmentBlock";
import { SummaryHero } from "./SummaryHero";
import { TreatmentBlock } from "./TreatmentBlock";
import { diagnosticSummary } from "./summaryData";

/**
 * O ato de cada bloco, ou seja, o lugar onde o número dele mora hoje.
 *
 * A aderência ao padrão, o plano de contas e a decisão do arquiteto sobre o
 * WRICEF são cards do Ato 1, e é lá que os links dos Blocos 2 e 3 levam, e não
 * aos Atos 2 e 3. O mapa é a única decisão: trocar o ato de
 * um bloco é trocar uma linha.
 */
const BLOCK_ACT = {
  environment: "environment",
  diagnosis: "environment",
  treatment: "environment",
  investment: "investment",
} as const satisfies Record<string, ActId>;

/**
 * A aba "Resumo · para o executivo" (EF-DIAG-004).
 *
 * Só lê: cada número vem dos mesmos seletores dos atos de origem, por
 * `diagnosticSummary`, sem chamada nova. Recebe os dados por props como os atos,
 * e o nome do cliente para a frase de abertura.
 */
export function SummaryTab({
  sections,
  validation,
  calibration,
  snapshotState,
  clientName,
  onGoToAct,
}: Pick<DiagnosticTabProps, "sections" | "validation" | "calibration" | "snapshotState"> & {
  clientName?: string;
  onGoToAct: (act: ActId) => void;
}) {
  const { t } = useTranslation();
  const summary = diagnosticSummary({ sections, validation, calibration });

  // Sem o snapshot, todas as fontes pareceriam ausentes, e o Resumo diria "aguardando"
  // e "não calibrado" sobre o que ainda não se sabe.
  if (snapshotState === "loading") {
    return (
      <p data-testid="summary-snapshot-loading" className="text-[13px] text-[#596b85]">
        {t("Loading…")}
      </p>
    );
  }
  if (snapshotState === "failed") {
    return (
      <p
        data-testid="summary-snapshot-error"
        className="rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]"
      >
        {t("Could not load the diagnostic.")}
      </p>
    );
  }

  return (
    <section data-testid="diagnostic-summary" aria-label={t("Debrief summary")} className="flex flex-col gap-4">
      <SummaryHero summary={summary} clientName={clientName ?? t("the client")} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <EnvironmentBlock
          summary={summary.environment}
          act={BLOCK_ACT.environment}
          onGoToAct={onGoToAct}
        />
        <DiagnosisBlock summary={summary.diagnosis} act={BLOCK_ACT.diagnosis} onGoToAct={onGoToAct} />
        <TreatmentBlock summary={summary.treatment} act={BLOCK_ACT.treatment} onGoToAct={onGoToAct} />
        <InvestmentBlock
          summary={summary.investment}
          act={BLOCK_ACT.investment}
          onGoToAct={onGoToAct}
        />
      </div>
    </section>
  );
}
